import React, { useEffect, useMemo, useRef, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { Stack } from 'react-bootstrap'
import { useAsync, useFullscreen, useToggle, useUnmount } from 'react-use'
import type { PivotDateRange, PivotLayout, PivotLayoutField, Template } from '@ztechapps/common.contract'
import type { PivotCellRenderer, PivotCellTextRenderer } from '../constants/types'
import type { PivotData, PivotExpandState, PivotMatrix } from '../pivot/types'
import { useDeepCompareEffect, useError, useUser, useUserTimeZone } from '@ztechapps/common.hook'
import { PivotChartTypeEnum } from '@ztechapps/common.contract'
import { downloadBlob, fetch, sanitizeFilename } from '@ztechapps/common.utils'
import { DataSuspense, EmptyContent, HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { PivotChart } from '../PivotChart'
import { exportChartPdf, exportChartPng } from '../PivotChart/pivotChartExport.utils'
import { PivotGrid } from '../PivotGrid'
import { OpenTemplateModal } from './OpenTemplateModal'
import { SaveTemplateModal } from './SaveTemplateModal'
import { EMPTY_PIVOT_LAYOUT, PIVOT_FULLSCREEN_GRID_MAX_HEIGHT, PIVOT_GRID_MAX_HEIGHT } from '../pivot/constants'
import { PivotExportFormatEnum } from '../pivot/PivotExportFormatEnum'
import { buildPivot } from '../pivot/buildPivot'
import { buildPivotMatrix } from '../pivot/buildPivotMatrix'
import { hasCollapsedPivotRow } from '../pivot/hasCollapsedPivotRow'
import { listPivotFilterChips } from '../pivot/listPivotFilterChips'
import { listPivotLoadedDays } from '../pivot/listPivotLoadedDays'
import { parsePivotLayout } from '../pivot/parsePivotLayout'
import { downloadPivot, printPivot } from '../pivot/pivotExport.utils'
import {
  cyclePivotValueSort,
  setPivotColumnWidth,
  setPivotLayoutFilter,
  setPivotLayoutValueFilter,
  setPivotMemberSort,
} from '../pivot/pivotLayout.utils'
import { sortPivotResult } from '../pivot/sortPivotResult'
import { toPivotData } from '../pivot/toPivotData'
import { usePivot } from '../usePivot'
import { useOpenPivotContext } from '../OpenPivot'
import { PivotViewerProvider } from './Context'
import { PivotFilterBar } from './PivotFilterBar'
import { PivotToolbar } from './PivotToolbar'
import { usePivotDefaultTemplate } from './usePivotDefaultTemplate'

export type OnChangeTemplateFunction = ({ template }: { template: Template }) => void

export type PivotViewerProps = {
  name?: string
  filter?: Record<string, any>
  url?: string
  templateCategory?: string
  onChangeTemplate?: OnChangeTemplateFunction
  renderCell?: PivotCellRenderer
  renderCellText?: PivotCellTextRenderer
  refreshTrigger?: number
  exportFileName?: string
  headerFilters?: string[]
  /** The report's filter template category — the choices for a pivot template's linked filter template. */
  filterCategory?: string
  /** Called with the linked filter template's id after a pivot template that auto-applies it loads. */
  onApplyFilterTemplate?: (filterTemplateId: string) => void
  /** Exact-name programmatic entry to the page's header-filter chain; unknown names no-op. */
  switchHeaderFilter: (headerFilter: string) => void
}

export const PivotViewer = ({
  name,
  filter,
  url = '/reports/pivot',
  templateCategory,
  onChangeTemplate,
  renderCell,
  renderCellText,
  refreshTrigger,
  exportFileName = 'pivot',
  headerFilters,
  filterCategory,
  onApplyFilterTemplate,
  switchHeaderFilter,
}: PivotViewerProps) => {
  const { accessToken } = useUser()
  const { onError } = useError()

  const { defaultLayout, loading: templateLoading } = usePivotDefaultTemplate({
    templateCategory,
    onChangeTemplate,
    onApplyFilterTemplate,
  })

  const { pivotUrl, refreshUrl } = usePivot({
    url,
    query: { name, filter },
    accessToken,
  })

  useEffect(() => {
    if (refreshTrigger) refreshUrl()
    // `refreshUrl` is deliberately excluded — `usePivot` recreates it every render (not
    // memoized), and it calls `setPivotUrl` internally. Adding it here would re-run this effect
    // on the very re-render its own `refreshUrl()` call causes, looping for as long as
    // `refreshTrigger` stays truthy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger])

  const {
    value: pivotResponse,
    loading: rowsLoading,
    error: rowsError,
  } = useAsync(
    async () => (isEmpty(pivotUrl) ? undefined : toPivotData(await fetch(pivotUrl, { method: 'GET' }))),
    [pivotUrl]
  )
  // the grid and the field panel both read fields and rows, so they share one empty stand-in before the rows arrive
  const data = useMemo<PivotData>(() => pivotResponse ?? { fields: [], rows: [] }, [pivotResponse])

  const [layout, setLayout] = useState<PivotLayout>(EMPTY_PIVOT_LAYOUT)

  useDeepCompareEffect(() => {
    if (defaultLayout) setLayout(defaultLayout)
  }, [defaultLayout])

  const [expandState, setExpandState] = useState<PivotExpandState>({ expandAll: false, toggledKeys: new Set() })

  useDeepCompareEffect(() => {
    setExpandState({ expandAll: layout.expandAll, toggledKeys: new Set<string>() })
  }, [layout.rows, layout.expandAll])

  const [showSave, toggleSave] = useToggle(false)
  const [showOpen, toggleOpen] = useToggle(false)

  const timeZone = useUserTimeZone()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  // state, not a ref: the toolbar's chart export items show only while a chart element is mounted
  const [chartElement, setChartElement] = useState<HTMLDivElement | null>(null)
  const [fullscreenOn, toggleFullscreen] = useToggle(false)
  // Esc leaves fullscreen in the browser, which never reaches the toggle — `onClose` resets it
  // so the button reads Fullscreen again. `container` stays undefined off-fullscreen so every
  // overlay keeps its default document.body portal until fullscreen needs the pivot root.
  const isFullscreen = useFullscreen(rootRef, fullscreenOn, { onClose: () => toggleFullscreen(false) })
  const container = isFullscreen ? (rootRef.current ?? undefined) : undefined

  const { rows, columns, measures, filters } = layout
  const result = useMemo(
    () => buildPivot({ data, layout: { rows, columns, measures, filters }, timeZone }),
    [data, rows, columns, measures, filters, timeZone]
  )
  // sorts and widths never rebuild the pivot itself — they reorder and size the built result
  const sortedResult = useMemo(
    () => sortPivotResult({ result, memberSorts: layout.memberSorts, valueSort: layout.valueSort }),
    [result, layout.memberSorts, layout.valueSort]
  )
  // the toggle reads the shown rows, not the layout flag — collapsing one group flips it back
  const hasCollapsedRow = useMemo(
    () => hasCollapsedPivotRow({ node: sortedResult.rowRoot, depth: sortedResult.rowFields.length, expandState }),
    [sortedResult, expandState]
  )

  // a stored column line shows as columns while fewer than two values are shown — display only, the layout keeps
  // it so Save and a later result with two values still see it
  const requestedChartType = layout.chartType ?? PivotChartTypeEnum.Grid
  const chartType =
    requestedChartType === PivotChartTypeEnum.ColumnLine && result.measures.length < 2
      ? PivotChartTypeEnum.Column
      : requestedChartType

  const onToggle = (key: string) => {
    setExpandState((prev) => {
      const toggledKeys = new Set(prev.toggledKeys)
      if (toggledKeys.has(key)) toggledKeys.delete(key)
      else toggledKeys.add(key)
      return { expandAll: prev.expandAll, toggledKeys }
    })
  }

  const onExpandAll = () => {
    setLayout({ ...layout, expandAll: true })
    setExpandState({ expandAll: true, toggledKeys: new Set() })
  }

  const onCollapseAll = () => {
    setLayout({ ...layout, expandAll: false })
    setExpandState({ expandAll: false, toggledKeys: new Set() })
  }

  const onFilter = (field: PivotLayoutField, excludedValues: string[], dateRange?: PivotDateRange) =>
    setLayout(setPivotLayoutFilter(layout, field, excludedValues, dateRange))
  const filterChips = useMemo(
    () => listPivotFilterChips({ data, layout: { rows, columns, measures, filters }, timeZone }),
    [data, rows, columns, measures, filters, timeZone]
  )

  // the chat reads the open pivot through this bridge — optional-chained, so a page with no
  // provider (the TotalMilk Hauler analyze page, unit and browser tests) registers nothing
  const loadedDays = useMemo(() => listPivotLoadedDays({ data, timeZone }), [data, timeZone])
  const { registerOpenPivot } = useOpenPivotContext()
  useDeepCompareEffect(() => {
    if (name !== undefined)
      registerOpenPivot?.({
        report: name,
        fields: data.fields,
        layout,
        loadedDays,
        headerFilters: headerFilters ?? [],
        apply: setLayout,
        switchHeaderFilter,
      })
    // `registerOpenPivot` is deliberately excluded — the provider holds it steady, and its own
    // `setOpenPivot` call would re-run this effect on the re-render it causes, looping for as
    // long as the layout stays put. `switchHeaderFilter` stays out for the same reason — a new
    // closure identity every render would re-register on every render; it closes over the template
    // list `headerFilters` derives from, so each run above already carries the current one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, data.fields, layout, loadedDays, headerFilters])
  useUnmount(() => registerOpenPivot?.(undefined))

  // exports and print read every row in the current expand state, not just the rows in view;
  // the filter chips lead the matrix as [label, text] rows, then one blank row
  const exportMatrix = (formatValues = false): PivotMatrix => {
    const matrix = buildPivotMatrix({
      result: sortedResult,
      expandState,
      showSubtotals: layout.showSubtotals,
      showGrandTotals: layout.showGrandTotals,
      formatValues,
    })
    return isEmpty(filterChips) ? matrix : [...filterChips.map(({ label, text }) => [label, text]), [], ...matrix]
  }

  // a capture forces the chart to the light scheme and restores it when done, so a second capture started
  // meanwhile would be left in the dark scheme and is ignored. A ref, never state: a re-render mid-capture
  // makes recharts lay the plot out again around the uncapped legend, and a tall legend leaves the export no pie
  const isExportingChart = useRef(false)
  const exportChart = async (toBlob: (element: HTMLElement) => Promise<Blob>, extension: string) => {
    if (isExportingChart.current) return
    isExportingChart.current = true
    try {
      downloadBlob(await toBlob(chartElement), `${sanitizeFilename(exportFileName)}.${extension}`)
    } catch (e) {
      onError(e)
    } finally {
      isExportingChart.current = false
    }
  }

  // with nowhere to pivot or nothing loaded, expand/collapse, format and the exports have nothing to act on
  const isLayoutEmpty = isEmpty(layout.rows) && isEmpty(layout.columns) && isEmpty(layout.measures)
  const isLoading = rowsLoading || templateLoading
  const hasGrid = !isLayoutEmpty && !isEmpty(data.rows)

  return (
    <Stack gap={2} ref={rootRef} className={isFullscreen ? 'bg-body p-2 overflow-auto' : undefined}>
      <PivotViewerProvider container={container}>
        <HorizontalStack gap={2} className="flex-wrap align-items-start">
          <PivotFilterBar
            chips={filterChips}
            layout={layout}
            data={data}
            timeZone={timeZone}
            onFilter={onFilter}
            onValueFilter={(field, valueFilter) => setLayout(setPivotLayoutValueFilter(layout, field, valueFilter))}
          />
          <PivotToolbar
            onOpen={() => toggleOpen(true)}
            onSave={() => toggleSave(true)}
            isLoading={isLoading}
            hasGrid={hasGrid}
            hasCollapsedRow={hasCollapsedRow}
            onExpandAll={onExpandAll}
            onCollapseAll={onCollapseAll}
            onExportExcel={() => downloadPivot(exportMatrix(false), exportFileName, PivotExportFormatEnum.Xlsx)}
            onExportCsv={() => downloadPivot(exportMatrix(false), exportFileName, PivotExportFormatEnum.Csv)}
            onPrint={() => {
              if (iframeRef.current) printPivot(exportMatrix(true), iframeRef.current)
            }}
            hasChart={chartElement !== null}
            onExportChartPng={() => exportChart(exportChartPng, 'png')}
            onExportChartPdf={() => exportChart(exportChartPdf, 'pdf')}
            data={data}
            layout={layout}
            shownMeasures={result.measures}
            timeZone={timeZone}
            onApplyLayout={setLayout}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => toggleFullscreen()}
            chartType={chartType}
            onChangeChartType={(next) => setLayout({ ...layout, chartType: next })}
          />
        </HorizontalStack>
        <DataSuspense loading={isLoading} error={rowsError}>
          {isEmpty(data.rows) ? (
            <EmptyContent message="No data" />
          ) : isLayoutEmpty ? (
            <EmptyContent message="Add fields to Rows, Columns or Measures" />
          ) : chartType !== PivotChartTypeEnum.Grid ? (
            <PivotChart
              result={sortedResult}
              expandState={expandState}
              chartType={chartType}
              maxHeight={isFullscreen ? PIVOT_FULLSCREEN_GRID_MAX_HEIGHT : PIVOT_GRID_MAX_HEIGHT}
              renderCellText={renderCellText}
              chartRef={setChartElement}
            />
          ) : (
            <PivotGrid
              result={sortedResult}
              expandState={expandState}
              showSubtotals={layout.showSubtotals}
              showGrandTotals={layout.showGrandTotals}
              maxHeight={isFullscreen ? PIVOT_FULLSCREEN_GRID_MAX_HEIGHT : PIVOT_GRID_MAX_HEIGHT}
              renderCell={renderCell}
              renderCellText={renderCellText}
              onToggle={onToggle}
              data={data}
              filters={layout.filters}
              timeZone={timeZone}
              onFilter={onFilter}
              columnWidths={layout.columnWidths}
              onColumnWidth={(target, width) => setLayout(setPivotColumnWidth(layout, target, width))}
              memberSorts={layout.memberSorts}
              onMemberSort={(field, direction) => setLayout(setPivotMemberSort(layout, field, direction))}
              valueSort={layout.valueSort}
              onValueSort={(valueColumn) => setLayout(cyclePivotValueSort(layout, valueColumn))}
              rootRef={rootRef}
            />
          )}
        </DataSuspense>
        <ShowIf condition={showSave}>
          <SaveTemplateModal
            category={templateCategory}
            filterCategory={filterCategory}
            initialValues={{ category: templateCategory, value: JSON.stringify(layout) }}
            show={showSave}
            onApply={({ template }) => onChangeTemplate?.({ template })}
            onClose={() => toggleSave(false)}
            container={container}
          />
        </ShowIf>
        <ShowIf condition={showOpen}>
          <OpenTemplateModal
            category={templateCategory}
            filterCategory={filterCategory}
            show={showOpen}
            onApply={({ template }) => {
              setLayout(parsePivotLayout(template.value))
              onChangeTemplate?.({ template })
              if (template.autoApplyFilterTemplate && template.filterTemplateId)
                onApplyFilterTemplate?.(template.filterTemplateId)
            }}
            onClose={() => toggleOpen(false)}
            container={container}
          />
        </ShowIf>
        <iframe ref={iframeRef} title="Pivot print" className="visually-hidden" aria-hidden="true" tabIndex={-1} />
      </PivotViewerProvider>
    </Stack>
  )
}
